import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RefundsReturns = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-gray-400 hover:text-white"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
          <div className="text-blue-500 font-bold text-xl">ShopNFCTags</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">
          Refund and Returns Policy
        </h1>

        {/* Refund Policy Section */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Refund Policy
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed">
            We want you to be completely satisfied with your purchase. If your item arrives defective or damaged, you are eligible for a refund under the following conditions:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                14-Day Refund Window:
              </h3>
              <p className="text-gray-400 leading-relaxed">
                You must request a refund within 14 days of receiving your order.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Return Authorization Required:
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Before returning any item, you must contact our customer service team at{' '}
                <a 
                  href="mailto:orders@shopnfctags.com" 
                  className="text-blue-500 hover:text-blue-400 transition-colors"
                >
                  orders@shopnfctags.com
                </a>{' '}
                to obtain a Return Authorization Number (RAN). Returns without a RAN will not be accepted.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Return Shipping:
              </h3>
              <p className="text-gray-400 leading-relaxed">
                The product must be returned in its original packaging and condition, at the customer's expense. You may consider using a trackable shipping service or purchasing shipping insurance. We don't guarantee that we will receive your returned item.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Inspection & Approval:
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Once your return is received and inspected, we will notify you of the approval or rejection of your refund. Approved refunds will be issued to your original payment method or you may request a replacement for the damaged item.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mt-6">
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">Please note:</strong> This policy applies only to defective or items damaged in transit. We do not offer refunds for buyer's remorse or incorrect orders.
              </p>
            </div>
          </div>
        </section>

        {/* Sale Items Section */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Sale Items
          </h2>
          <p className="text-gray-400 mb-4 leading-relaxed">
            Only regular priced items may be refunded. Sale items cannot be refunded.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Once your return is received and inspected, we will send you an email to notify you that we have received your returned item. We will also notify you of the approval or rejection of your refund.
          </p>
        </section>

        {/* Refund Processing Section */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-lg p-8">
            <p className="text-gray-300 leading-relaxed mb-4">
              If you are approved, then your refund will be processed, and a credit will automatically be applied to your credit card or original method of payment, or your replacement item will be shipped within a few days.
            </p>
            <p className="text-gray-300 leading-relaxed">
              If you have any questions, don't hesitate to contact us at{' '}
              <a 
                href="mailto:orders@shopnfctags.com" 
                className="text-blue-500 hover:text-blue-400 transition-colors"
              >
                orders@shopnfctags.com
              </a>.
            </p>
          </div>
        </section>

        {/* Late or Missing Refunds Section */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Late or Missing Refunds
          </h2>
          <p className="text-gray-400 leading-relaxed">
            If you haven't received a refund yet, first check your bank account again. Then contact your credit card company, it may take some time before your refund is officially posted. Next contact your bank. There is often some processing time before a refund is posted. If you've done all of this and you still have not received your refund yet, please contact us at{' '}
            <a 
              href="mailto:orders@shopnfctags.com" 
              className="text-blue-500 hover:text-blue-400 transition-colors"
            >
              orders@shopnfctags.com
            </a>.
          </p>
        </section>

        {/* Contact CTA */}
        <section className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg p-8 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-white" />
          <h3 className="text-2xl font-bold text-white mb-3">
            Questions About Returns?
          </h3>
          <p className="text-white/90 mb-6">
            Our customer service team is here to help
          </p>
          <Button
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
            onClick={() => window.location.href = 'mailto:orders@shopnfctags.com'}
          >
            Contact Us at orders@shopnfctags.com
          </Button>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 ShopNFCTags. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RefundsReturns;
